import React, { useCallback, useEffect } from "react";
import _ from "lodash";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import {
  changeColorToSave,
  changeTaglineToSave,
  getWebContentPreview,
  loadLogo,
  resetToSave,
  saveBranding,
} from "./branding.actions";
import { loadWebContent } from "../webFilteringForm/webContent.actions";
import { TabControls } from "../../components/tabControls/TabControls";
import { withPromptDialog } from "../../../../hocs/withPrompt";
import { useBrandingForm } from "./useBrandingForm";
import { useMount } from "@sportal/cdk/hooks";
import { previewPage } from "../shared.actions";
import { Tagline } from "./sections/Tagline";
import { getBrandingValidation } from "./branding.selectors";
import { Logo } from "./sections/Logo";
import { Color } from "./sections/Color";
import { clearLogoFromStorage } from "../../components";
import { Form } from "../../../../components/form";
import { Desktop, TabletMobile } from "../../../../hocs/Responsive";
import { AriaPageTitle } from "../../../../components/ariaPageTitle";

import styles from "./BrandingForm.scss";

const { DEFAULT_COLOR } = styles;

const BrandingFormContent = withPromptDialog(
  ({ hasChanges, save, discard }) => {
    const dispatch = useDispatch();
    const validation = useSelector(getBrandingValidation, _.isEqual);

    const { color, tagline, logo, toSave, isValid } = useBrandingForm();

    useMount(() => {
      if (!color && !tagline) {
        dispatch(loadWebContent());
      }

      if (!logo) {
        dispatch(loadLogo());
      }
    });

    const preview = async () =>
      await previewPage(dispatch, getWebContentPreview(), "web-filtering");

    return (
      <section className="branding">
        <AriaPageTitle pageKey="page_title_service_page_branding" />
        <Desktop>
          <h1 className={"blocked-page__title"}>
            <FormattedMessage id={"branding_setup"} />
          </h1>
        </Desktop>
        <TabletMobile>
          <h2 className={"blocked-page__title"}>
            <FormattedMessage id={"branding_setup"} />
          </h2>
        </TabletMobile>
        <Form
          onSubmit={save}
          name={"brandingForm"}
          description={<FormattedMessage id={"branding_description"} />}
        >
          <Tagline
            tagline={
              toSave.tagline || toSave.tagline === "" ? toSave.tagline : tagline
            }
            onChange={(value) => dispatch(changeTaglineToSave(value))}
            validation={validation.tagline}
          />
          <div className="logo-color-wrapper">
            <Logo />
            <Color
              color={toSave.color || color || DEFAULT_COLOR}
              onChange={(value) => dispatch(changeColorToSave(value))}
            />
          </div>

          <TabControls
            isFormValid={isValid}
            isFormUnchanged={!hasChanges}
            onReset={discard}
            onPreview={preview}
          />
        </Form>
      </section>
    );
  }
);

export const BrandingForm = () => {
  const dispatch = useDispatch();
  const { hasChanges, isValid } = useBrandingForm();

  const discard = useCallback(() => {
    clearLogoFromStorage();
    dispatch(resetToSave());
  }, [dispatch]);
  const save = () => dispatch(saveBranding());

  useEffect(() => {
    return () => discard();
  }, [discard]);

  return (
    <BrandingFormContent
      hasChanges={hasChanges && isValid}
      save={save}
      discard={discard}
    />
  );
};
